import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  BookOpen, 
  FileText, 
  LogOut,
  Settings,
  HelpCircle,
  Menu,
  X
} from 'lucide-react';

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const isDragging = useRef(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Swipe-to-close handlers
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    isDragging.current = true;
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    // Only allow swiping left (to close)
    if (diff < 0 && drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${diff}px)`;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = touchCurrentX.current - touchStartX.current;
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'transform 300ms ease-in-out';
    }
    // Close if swiped more than 80px to the left
    if (diff < -80) {
      setMobileOpen(false);
    } else if (drawerRef.current) {
      drawerRef.current.style.transform = 'translateX(0)';
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const studentLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/courses', icon: BookOpen, label: 'Courses' },
    { to: '/assignments', icon: FileText, label: 'Assignments' },
    { to: '/questions', icon: HelpCircle, label: 'Questions' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const visitorLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/courses', icon: BookOpen, label: 'Courses' },
    { to: '/assignments', icon: FileText, label: 'Assignments' },
    { to: '/questions', icon: HelpCircle, label: 'Questions' },
  ];

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Admin Dashboard' },
    { to: '/courses', icon: BookOpen, label: 'Courses' },
  ];

  const links = user?.role === 'admin' ? adminLinks : user?.role === 'visitor' ? visitorLinks : studentLinks;

  const sidebarContent = (prefix = '') => (
    <>
      {/* Logo */}
      <div className="p-5 lg:p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_edu-storage-core/artifacts/k2aah68p_ChatGPT%20Image%20Mar%207%2C%202026%20at%2002_55_17%20PM.png" 
            alt="GIHDS Logo" 
            className="h-10 w-auto"
          />
          <div>
            <h1 className="text-sm font-semibold leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              GIHDS
            </h1>
            <p className="text-xs text-primary-foreground/60">Learning Portal</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 lg:p-4 space-y-1">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
            data-testid={`${prefix}sidebar-${link.label.toLowerCase().replace(' ', '-')}`}
          >
            <link.icon className="w-5 h-5" />
            <span>{link.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User info & Logout */}
      <div className="p-4 border-t border-white/10">
        <div className="mb-4 px-4">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-primary-foreground/60 truncate">{user?.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 bg-white/10 rounded text-xs capitalize">
              {user?.role}
            </span>
            {user?.program && user?.role === 'student' && (
              <span className="px-2 py-0.5 bg-white/15 rounded text-xs">
                {user.program === 'doctor' ? 'Doctoral' : user.program === 'mdppm' ? 'MDPPM' : 'Master'}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-left hover:bg-destructive/20"
          data-testid={`${prefix}logout-button`}
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground flex items-center justify-between px-4 h-14" data-testid="mobile-header">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_edu-storage-core/artifacts/k2aah68p_ChatGPT%20Image%20Mar%207%2C%202026%20at%2002_55_17%20PM.png" 
            alt="GIHDS Logo" 
            className="h-8 w-auto"
          />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>GIHDS</span>
        </div>
        <button 
          onClick={() => setMobileOpen(!mobileOpen)} 
          className="p-2 hover:bg-white/10 rounded-sm transition-colors"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside 
        ref={drawerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`lg:hidden fixed top-0 left-0 z-50 w-72 max-w-[85vw] h-full bg-primary text-primary-foreground flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="mobile-sidebar"
      >
        {/* Close button inside drawer */}
        <div className="absolute top-4 right-4">
          <button 
            onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-white/10 rounded-sm"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarContent('mobile-')}
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 h-screen bg-primary text-primary-foreground flex-col flex-shrink-0 sticky top-0 overflow-y-auto" data-testid="sidebar">
        {sidebarContent('')}
      </aside>
    </>
  );
}
